import { Size } from '@ps/web-core-enums';
import { useScreenSize } from '@ps/web-shared-components';
import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';

import {
  PKR_LOADERS,
  useGetCurrentClub,
  useHomeGamesWidget,
  useSetLoadingOnUnmount
} from '@swf/poker';

import type { Props } from '../Container';
import SeasonsDropdown from '../SeasonsDropdown/SeasonsDropdown';
import useSeason from '../Standings/hooks/useSeason';

import NoResults from './NoResults/NoResults';
import styles from './Results.scss';
import ResultsList from './ResultsList/ResultsList';
import ResultsSkeleton from './ResultsSkeleton';

const Summaries: React.FC<{ loading: boolean; noData: boolean }> = ({ loading, noData }) => {
  if (loading) return <ResultsSkeleton />;
  if (!loading && noData) return <NoResults />;

  return <ResultsList />;
};

const Results: React.FC<Props> = () => {
  const {
    params: { clubId }
  } = useRouteMatch<{ clubId: string }>();
  const { clubName } = useGetCurrentClub(clubId);
  const { widgetSelect, widgetDispatch } = useHomeGamesWidget();
  const { handleSeasonSelect, noSeasons, seasonId, seasonName } = useSeason();
  const { clubSeasons: clubSeasonsLoading, clubSeasonSummaries: clubSeasonSummariesLoading } =
    useSelector(widgetSelect.loading);
  const summaries = useSelector(widgetSelect.clubSummariesByDate);
  const noSummaries = isEmpty(summaries);
  const { greaterThan } = useScreenSize();
  const isMobile = !greaterThan(Size.XXSmall);

  useSetLoadingOnUnmount(PKR_LOADERS.clubSeasonSummaries);

  useEffect(() => {
    widgetDispatch.fetchClubSeasons({
      clubId,
      latestOnly: true
    });
  }, [widgetDispatch, clubId]);

  useEffect(() => {
    if (noSeasons || !clubId || !seasonId) return;

    widgetDispatch.fetchRecentTournamentSummaries({
      clubId: Number(clubId),
      seasonId: Number(seasonId)
    });
  }, [widgetDispatch, clubId, seasonId, noSeasons]);

  if (noSeasons && (!clubSeasonsLoading || !clubSeasonSummariesLoading)) {
    return <NoResults />;
  }

  return (
    <div className={styles.container}>
      {isMobile ? (
        <div className={styles.dropdownSection}>
          <div className={styles.title}>{`${clubName} ${seasonName}`}</div>
          <SeasonsDropdown onSelect={handleSeasonSelect} />
        </div>
      ) : (
        <SeasonsDropdown onSelect={handleSeasonSelect} />
      )}
      <Summaries loading={clubSeasonSummariesLoading} noData={noSummaries} />
    </div>
  );
};

export default Results;
